// Budget page — entry point for the four URL-addressable routes
// /budget, /budget/forecast, /budget/review and /budget/slide (design-note
// §7, workbook-parity §5). Each route renders this shell with a `view` prop;
// the internal tab bar navigates between them (Reconciliation's tab pattern,
// URL-addressable).
//
// Owns:
//   - fiscal-year state (default = current Feb-start FY), shared by all tabs
//   - the SINGLE EventSource subscription for the budget module, filtering
//     budget.dashboard_updated / budget.classification_updated /
//     budget.slide_updated and bumping per-view refresh keys (dashboard and
//     slide only when the hash actually changed)
//   - the workbook Import button (→ ImportReportModal) and the Export .xlsx
//     download link in the header
//   - toast state for child views
//
// Talks to the backend through `frontend/src/budget/api.js`. If a route 404s
// (backend not yet shipped), the affected view degrades to an empty state.

import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavHeader from '../components/NavHeader';
import { TOKENS, UI_FONT, MONO_FONT } from '../reconciliation/tokens';
import { Icon, Toast, styles } from '../reconciliation/primitives';
import DashboardView from '../budget/DashboardView';
import ForecastView from '../budget/ForecastView';
import ReviewQueueView from '../budget/ReviewQueueView';
import SlideView from '../budget/SlideView';
import ActualToBudgetView from '../budget/ActualToBudgetView';
import ImportReportModal from '../budget/ImportReportModal';
import { exportWorkbookUrl } from '../budget/api';
import { clearAllBudgetData } from '../api';
import { useSseSubscription } from '../context/SseContext';
import '../budget/Budget.css';

// FY N runs Feb N .. Jan N+1 (hardcoded Feb-start convention, same as the
// backend's fiscal helpers) — so January belongs to the previous fiscal year.
function currentFiscalYear() {
  const d = new Date();
  return d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
}

function fiscalYearOptions() {
  const fy = currentFiscalYear();
  return [fy + 1, fy, fy - 1, fy - 2];
}

const TABS = [
  { id: 'actualToBudget', label: 'Budget Workbench', path: '/budget/actual-to-budget' },
  { id: 'dashboard', label: 'Dashboard', path: '/budget' },
  { id: 'forecast', label: 'Forecast', path: '/budget/forecast' },
  { id: 'review', label: 'Review queue', path: '/budget/review' },
  { id: 'slide', label: 'Slide Page', path: '/budget/slide' },
];

// Destructive QA-reset button — mirrors KanbanBoard's DeleteAllInvoicesButton.
function DeleteAllBudgetButton({ onClick, busy }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      title="Delete every budget line, snapshot, slide group, amount and classification mapping, and unlink invoices / SAP rows. QA reset only — cannot be undone."
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 11px',
        borderRadius: 6,
        background: 'oklch(0.30 0.13 25)',
        color: '#fff',
        border: '1px solid oklch(0.50 0.18 25)',
        fontFamily: UI_FONT,
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: '0.01em',
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.7 : 1,
        boxShadow: busy
          ? 'none'
          : '0 0 0 1px oklch(0.58 0.18 25 / 0.25), 0 1px 2px rgba(0,0,0,0.25)',
      }}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
      </svg>
      {busy ? 'Deleting…' : 'Delete all budget data'}
    </button>
  );
}

export default function Budget({ view = 'dashboard' }) {
  // The page is the Budget Workbench only: hide the tab bar and force the
  // workbench view. The other views (dashboard/forecast/review/slide) stay
  // wired below but are unreachable until SHOW_TABS is flipped back on.
  const SHOW_TABS = false;
  const activeView = SHOW_TABS ? view : 'actualToBudget';
  const navigate = useNavigate();
  const [fiscalYear, setFiscalYear] = useState(currentFiscalYear);
  const [fyOptions] = useState(fiscalYearOptions);
  const [toast, setToast] = useState(null);
  const [clearingBudget, setClearingBudget] = useState(false);

  // Per-view refresh keys, bumped by SSE events so only the affected view
  // refetches.
  const [dashKey, setDashKey] = useState(0);
  const [reviewKey, setReviewKey] = useState(0);
  const [slideKey, setSlideKey] = useState(0);
  const [a2bKey, setA2bKey] = useState(0);

  // Workbook import: the chosen File drives the ImportReportModal, which
  // owns the POST and the force / expected-total re-submits (§2.5).
  const [importFile, setImportFile] = useState(null);
  const filePickerRef = useRef(null);

  // Refs so the shared-stream subscription handler sees current values
  // without resubscribing on every FY change / dashboard fetch.
  const fyRef = useRef(fiscalYear);
  fyRef.current = fiscalYear;
  const dashHashRef = useRef(null);
  const handleDashboardHash = useCallback((hash) => {
    dashHashRef.current = hash;
  }, []);
  const slideHashRef = useRef(null);
  const handleSlideHash = useCallback((hash) => {
    slideHashRef.current = hash;
  }, []);
  const a2bHashRef = useRef(null);
  const handleA2bHash = useCallback((hash) => {
    a2bHashRef.current = hash;
  }, []);

  // SSE — listen on the shared connection and bump per-view refresh keys.
  useSseSubscription((e) => {
    let msg;
    try {
      msg = JSON.parse(e.data);
    } catch {
      return;
    }
    if (msg.event === 'budget.dashboard_updated') {
      // Refetch only when the projection hash for the selected FY
      // actually changed (hash-unchanged rebuilds skip the broadcast
      // server-side, but another FY's events still reach us).
      if (msg.fiscal_year === fyRef.current && msg.hash !== dashHashRef.current) {
        setDashKey((k) => k + 1);
      }
    } else if (msg.event === 'budget.slide_updated') {
      if (msg.fiscal_year === fyRef.current && msg.hash !== slideHashRef.current) {
        setSlideKey((k) => k + 1);
      }
    } else if (msg.event === 'budget.actual_to_budget_updated') {
      if (msg.fiscal_year === fyRef.current && msg.hash !== a2bHashRef.current) {
        setA2bKey((k) => k + 1);
      }
    } else if (msg.event === 'budget.classification_updated') {
      if (msg.fiscal_year == null || msg.fiscal_year === fyRef.current) {
        setReviewKey((k) => k + 1);
        if (msg.classified != null || msg.unclassified != null) {
          setToast({
            text: `Classification run finished — ${msg.classified ?? 0} classified, ${
              msg.unclassified ?? 0
            } left to review`,
            tone: 'good',
          });
        }
      }
    }
  });

  const handleFyChange = (e) => {
    dashHashRef.current = null;
    slideHashRef.current = null;
    a2bHashRef.current = null;
    setFiscalYear(Number(e.target.value));
  };

  // SSE already refetches mounted views on the import's rebuild broadcasts;
  // this is the belt for a dropped connection.
  const handleImported = useCallback(() => {
    setDashKey((k) => k + 1);
    setSlideKey((k) => k + 1);
  }, []);

  const handleFilePicked = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) setImportFile(file);
    e.target.value = ''; // allow re-picking the same file later
  };

  const handleClearAllBudget = async () => {
    const confirmed = window.confirm(
      'Delete ALL budget data? This wipes every budget line, snapshot, slide group, amount, and classification mapping, and unlinks any invoices / SAP rows from budget lines. This cannot be undone.'
    );
    if (!confirmed) return;
    setClearingBudget(true);
    try {
      await clearAllBudgetData();
      // Force every mounted view to refetch the now-empty state.
      setDashKey((k) => k + 1);
      setSlideKey((k) => k + 1);
      setReviewKey((k) => k + 1);
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || 'Delete all budget data failed';
      setToast({ text: msg, tone: 'bad' });
    } finally {
      setClearingBudget(false);
    }
  };

  return (
    <>
      <NavHeader
        endActions={
          <DeleteAllBudgetButton onClick={handleClearAllBudget} busy={clearingBudget} />
        }
      />
      <div className="budget-page">
        {/* Header bar */}
        <div
          style={{
            padding: '14px 18px 0',
            background: TOKENS.panel,
            flex: '0 0 auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              fontFamily: UI_FONT,
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.015em',
            }}
          >
            <span>Budget Workbench ·</span>
            <select
              value={fiscalYear}
              onChange={handleFyChange}
              style={{
                fontFamily: MONO_FONT,
                fontSize: 14,
                fontWeight: 600,
                color: TOKENS.ink,
                background: TOKENS.panelAlt,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 6,
                padding: '3px 8px',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              {(fyOptions.includes(fiscalYear) ? fyOptions : [fiscalYear, ...fyOptions]).map(
                (fy) => (
                  <option key={fy} value={fy}>
                    FY{fy}
                  </option>
                )
              )}
            </select>
            <div
              style={{
                marginLeft: 'auto',
                alignSelf: 'center',
                display: 'flex',
                gap: 8,
              }}
            >
              <input
                ref={filePickerRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: 'none' }}
                onChange={handleFilePicked}
              />
              <button
                style={styles.ghostBtn}
                onClick={() => filePickerRef.current && filePickerRef.current.click()}
              >
                <Icon d="M12 16V4m0 0L7 9m5-5 5 5M4 20h16" size={12} sw={1.8} />
                Import workbook
              </button>
              <a
                href={exportWorkbookUrl(fiscalYear)}
                style={{ ...styles.ghostBtn, textDecoration: 'none' }}
              >
                <Icon d="M12 4v12m0 0 5-5m-5 5-5-5M4 20h16" size={12} sw={1.8} />
                Export .xlsx
              </a>
            </div>
          </div>
          <div
            style={{
              fontFamily: UI_FONT,
              fontSize: 11.5,
              color: TOKENS.ink3,
              marginTop: 2,
            }}
          >
            Budget vs Actual vs Committed by owner, category and line — Feb-start fiscal year.
          </div>
        </div>

        {/* Tabs row — marginBottom provides the 14px gap that used to live as
             padding-top on .budget-scroll. Keeping it outside the scroll
             container ensures it never scrolls away and sticky headers pin
             flush to the bottom border of this bar. */}
        {SHOW_TABS && (
        <div
          style={{
            padding: '4px 18px 0',
            marginBottom: 14,
            background: TOKENS.panel,
            borderBottom: `1px solid ${TOKENS.border}`,
            flex: '0 0 auto',
            display: 'flex',
            gap: 0,
          }}
        >
          {TABS.map((t) => {
            const active = t.id === view;
            return (
              <button
                key={t.id}
                onClick={() => navigate(t.path)}
                style={{
                  position: 'relative',
                  padding: '10px 16px',
                  border: 'none',
                  background: 'transparent',
                  fontFamily: UI_FONT,
                  fontSize: 12.5,
                  fontWeight: active ? 600 : 500,
                  color: active ? TOKENS.ink : TOKENS.ink2,
                  cursor: 'pointer',
                }}
              >
                {t.label}
                {active && (
                  <span
                    style={{
                      position: 'absolute',
                      left: 12,
                      right: 12,
                      bottom: -1,
                      height: 2,
                      background: TOKENS.ink,
                      borderRadius: 1,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
        )}

        <div className="budget-scroll">
          {activeView === 'dashboard' && (
            <DashboardView
              fiscalYear={fiscalYear}
              refreshKey={dashKey}
              onHash={handleDashboardHash}
            />
          )}
          {activeView === 'forecast' && <ForecastView fiscalYear={fiscalYear} onToast={setToast} />}
          {activeView === 'review' && (
            <ReviewQueueView fiscalYear={fiscalYear} refreshKey={reviewKey} onToast={setToast} />
          )}
          {activeView === 'slide' && (
            <SlideView
              fiscalYear={fiscalYear}
              refreshKey={slideKey}
              onHash={handleSlideHash}
              onToast={setToast}
            />
          )}
          {activeView === 'actualToBudget' && (
            <ActualToBudgetView
              fiscalYear={fiscalYear}
              refreshKey={a2bKey}
              onHash={handleA2bHash}
              onToast={setToast}
            />
          )}
        </div>
      </div>

      {importFile && (
        <ImportReportModal
          file={importFile}
          fiscalYear={fiscalYear}
          onClose={() => setImportFile(null)}
          onImported={handleImported}
          onToast={setToast}
        />
      )}

      {toast && (
        <Toast text={toast.text} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
